// Pure reorder logic for one column of the Theory of Change board.
//
// The board renders each step from a Position-ordered Firestore query
// (useFirestoreOrderBy(collection, 'Position', 'asc')), and the portfolio
// subscribe modal reads the same field with the same order — so Position IS the
// order the user sees everywhere. Dragging a card therefore has to renumber
// Position, not some board-local index.
//
// Returns only the docs whose Position actually changes, so a drag near the top
// of a long column doesn't rewrite the whole column. A doc missing Position is
// also included — defensively. The board can't actually surface one (its query
// orders by Position server-side, so such a doc never arrives:
// docs/pitfalls.md#database-firestore-orderby-excludes-missing-field), but the
// helper is pure and a caller that feeds it an unordered list should get the
// field backfilled rather than a doc silently left out of the renumbering.

/**
 * @typedef {object} PositionWrite
 * @property {string} docid - Firestore document id to update.
 * @property {number} Position - The new 1-based position.
 */

/**
 * Move `activeId` to `overId`'s slot within one column and return the resulting
 * Position writes, in their new order.
 *
 * @param {Array<{id: string, raw: {docid?: string, Position?: number}}>} [nodes] - The column's nodes, in their current (Position) order.
 * @param {string} activeId - ID of the dragged node.
 * @param {string} overId - ID of the node it was dropped on.
 * @returns {Array<PositionWrite>} Empty when the drag is a no-op or either id is unknown.
 */
const reorderStepPositions = (nodes, activeId, overId) => {
    const list = Array.isArray(nodes) ? nodes : []
    if (!activeId || activeId === overId) return []

    const from = list.findIndex((n) => n?.id === activeId)
    const to = list.findIndex((n) => n?.id === overId)
    if (from === -1 || to === -1) return []

    const next = [...list]
    next.splice(to, 0, next.splice(from, 1)[0])

    return next
        .map((node, index) => ({ node, Position: index + 1 }))
        .filter(({ node, Position }) => node?.raw?.docid && node.raw.Position !== Position)
        .map(({ node, Position }) => ({ docid: node.raw.docid, Position }))
}

export default reorderStepPositions
